"use client";

/**
 * UserSearchInput — autocomplete from the users collection. Searches by
 * displayName, email, and uid (typed value treated as ID match too).
 * Mirrors the pattern used by ArtistInput / GenresTagInput.
 *
 * Use anywhere an admin needs to attach an existing user to something
 * (credits, managers, claims) so they pick a real row instead of typing
 * a free-form string that creates a duplicate ghost.
 */

import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { db } from "@/lib/firebase";
import { collection, getDocs, limit, query } from "firebase/firestore";
import { cn } from "@/lib/utils";

export interface UserSearchResult {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
}

interface Props {
  value: string;                                    // current display label
  onPick: (user: UserSearchResult) => void;         // user picked from dropdown
  onChangeText?: (raw: string) => void;             // free-text edits (no DB pick)
  placeholder?: string;
  className?: string;
  /** Inline label rendered above the field. Optional — caller can provide own. */
  label?: string;
}

// Cache the user list per-page-mount so repeated searches don't re-fetch.
let _usersCache: UserSearchResult[] | null = null;
async function fetchAllUsers(): Promise<UserSearchResult[]> {
  if (_usersCache) return _usersCache;
  try {
    const snap = await getDocs(query(collection(db, "users"), limit(5000)));
    _usersCache = snap.docs.map(d => {
      const v = d.data() as any;
      return {
        uid: d.id,
        displayName: v.displayName || v.fullName || v.name || "",
        email: (v.email || "").toLowerCase(),
        photoURL: v.profileImageUrl || v.photoURL || v.avatarUrl || "",
      };
    });
    return _usersCache;
  } catch (err) {
    console.error("[UserSearchInput] failed to load users", err);
    return [];
  }
}

export default function UserSearchInput({
  value,
  onPick,
  onChangeText,
  placeholder = "Search by name, email, or paste user ID…",
  className,
  label,
}: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [users, setUsers] = useState<UserSearchResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setDraft(value); }, [value]);

  // Prefetch users on first focus.
  const ensureLoaded = async () => {
    if (users.length === 0) {
      const list = await fetchAllUsers();
      setUsers(list);
    }
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const q = draft.toLowerCase().trim();
  const matches = q
    ? users
        .filter(u =>
          u.displayName.toLowerCase().includes(q) ||
          u.email.includes(q) ||
          u.uid.toLowerCase().includes(q)
        )
        .slice(0, 12)
    : users.slice(0, 12);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {label && <label className="text-xs text-muted-foreground mb-1 block">{label}</label>}
      <Input
        value={draft}
        placeholder={placeholder}
        onFocus={() => { ensureLoaded(); setOpen(true); }}
        onChange={(e) => {
          setDraft(e.target.value);
          onChangeText?.(e.target.value);
          if (!open) setOpen(true);
        }}
        className="h-9 text-sm"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border/60 bg-popover shadow-lg">
          {matches.map(u => (
            <button
              key={u.uid}
              type="button"
              onClick={() => {
                onPick(u);
                setDraft(u.displayName || u.email || u.uid);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
            >
              {u.photoURL ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={u.photoURL} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-muted-foreground/20 flex items-center justify-center text-[10px] font-semibold">
                  {(u.displayName || u.email || u.uid).slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{u.displayName || "(no name)"}</div>
                <div className="text-[10px] text-muted-foreground truncate">{u.email || u.uid}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && q && matches.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border/60 bg-popover shadow-lg p-3 text-xs text-muted-foreground">
          No matching user. Paste a UID directly if you know it.
        </div>
      )}
    </div>
  );
}
